import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthResponse, User } from '@jungles/shared';
import { botIntents } from '../lib/bot-intent';
import { request } from '../lib/api';
import { ApiError, api, loadToken, setToken } from '../lib/api';
import { failStartup } from '../lib/startup';
import { guessTimezone, isTelegram, syncTelegramTheme, tg } from '../lib/telegram';

interface SessionValue {
  user: User | null;
  botLaunchReady: boolean;
  status: 'loading' | 'anonymous' | 'authorized';
  /** Вход через Telegram: initData уходит на сервер и проверяется подписью. */
  loginWithTelegram: () => Promise<void>;
  /** Локальный вход для разработки вне Telegram. */
  loginAsDev: (name: string) => Promise<void>;
  logout: () => void;
  updateUser: (next: User) => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const sessionEpoch = useRef(0);
  const activeOwner = useRef<string | null>(null);
  const [botChecked, setBotChecked] = useState<string | null>(null);
  const [botFailed, setBotFailed] = useState<string | null>(null);
  const userRevision = useRef(0);
  const botAttempts = useRef(new Map<string, Promise<{ verified: boolean | null; timezone?: string }>>());
  const [status, setStatus] = useState<SessionValue['status']>('loading');

  const applyTheme = useCallback((next: User | null) => {
    const theme = next?.theme ?? 'dark';
    document.documentElement.dataset.theme = theme;
    // Mirror the existing server preference for the next pre-React paint.
    try { localStorage.setItem('jungles.theme', theme); } catch { /* Private mode. */ }
    syncTelegramTheme(theme);
  }, []);

  const adopt = useCallback(
    (response: AuthResponse) => {
      sessionEpoch.current++;
      if (activeOwner.current && activeOwner.current !== response.user.id) botIntents.clear();
      activeOwner.current = response.user.id;
      setToken(response.token);
      setUser(response.user);
      setStatus('authorized');
      applyTheme(response.user);
    },
    [applyTheme],
  );

  const loginWithTelegram = useCallback(async () => {
    const initData = tg()?.initData;
    if (!initData) throw new Error('Telegram недоступен');
    const epoch = sessionEpoch.current;
    const response = await api.post<AuthResponse>('/api/auth/telegram', {
      initData,
      timezone: guessTimezone(),
    });
    if (epoch === sessionEpoch.current) adopt(response);
  }, [adopt]);

  const loginAsDev = useCallback(
    async (name: string) => {
      const response = await api.post<AuthResponse>('/api/auth/telegram', {
        dev: { name },
        timezone: guessTimezone(),
      });
      adopt(response);
    },
    [adopt],
  );

  const logout = useCallback(() => {
    sessionEpoch.current++;
    activeOwner.current = null;
    const raw = tg()?.initData;
    if (raw) botIntents.suppress(raw);
    else botIntents.clear();
    setBotChecked(null);
    setToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const epoch = sessionEpoch.current;
      const auth = loadToken();
      const me = await api.get<User>('/api/me');
      if (epoch !== sessionEpoch.current || auth !== loadToken()) return;
      if (activeOwner.current && activeOwner.current !== me.id) botIntents.clear();
      activeOwner.current = me.id;
      setUser(me);
      setStatus('authorized');
      applyTheme(me);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) logout();
      else throw error;
    }
  }, [applyTheme, logout]);

  const updateUser = useCallback(
    (next: User) => {
      if (activeOwner.current !== next.id) return;
      userRevision.current++;
      setUser(next);
      applyTheme(next);
    },
    [applyTheme],
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (loadToken()) {
        try {
          const me = await api.get<User>('/api/me');
          if (cancelled) return;
          activeOwner.current = me.id;
          setUser(me);
          setStatus('authorized');
          applyTheme(me);
          // Refresh only a signed photo for this existing owner. This is not
          // login: no token adoption, timezone reset, or unsafe SDK identity.
          const initData = tg()?.initData;
          if (initData) {
            const epoch = sessionEpoch.current;
            const token = loadToken();
            void api.post<Pick<User, 'photoUrl'>>('/api/me/telegram-photo', { initData })
              .then(({ photoUrl }) => {
                if (cancelled || sessionEpoch.current !== epoch || loadToken() !== token) return;
                setUser(current => current?.id === me.id ? { ...current, photoUrl } : current);
              })
              .catch(() => { /* Optional photo refresh never invalidates a valid session. */ });
          }
          return;
        } catch (error) {
          if (cancelled) return;
          if (!(error instanceof ApiError && error.status === 401) && failStartup()) return;
          setToken(null);
        }
      }

      // В Telegram вход происходит сам: подпись уже есть, спрашивать нечего.
      if (isTelegram()) {
        try {
          await loginWithTelegram();
          return;
        } catch {
          if (cancelled || failStartup()) return;
          // Покажем экран входа с кнопкой и понятной ошибкой.
        }
      }

      if (!cancelled) {
        setStatus('anonymous');
        applyTheme(null);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applyTheme, loginWithTelegram]);

  const botIdentity = user ? `${user.id}:${loadToken() ?? ''}` : null;
  useEffect(() => {
    if (status !== 'authorized' || !user || !botIdentity) return;
    if (activeOwner.current && activeOwner.current !== user.id) botIntents.clear();
    activeOwner.current = user.id;
    const epoch = sessionEpoch.current;
    const owner = user.id;
    const identity = botIdentity;
    const revision = userRevision.current;
    const initData = tg()?.initData;
    let cancelled = false;
    if (!initData) { setBotChecked(identity); return; }
    // One bounded request per signed launch + authenticated identity in this boot.
    // StrictMode effect re-entry subscribes to the same promise, never starts a loop.
    const key = `${identity}:${initData}`;
    let attempt = botAttempts.current.get(key);
    if (!attempt) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      attempt = request<{ok?:boolean;accepted?:boolean;timezone?:string} | false>('/api/me/bot-open', { method: 'POST', body: { initData, timezone: guessTimezone() }, signal: controller.signal })
        .then(result => ({ verified: result !== false && result.accepted !== false, timezone: result ? result.timezone : undefined }))
        .catch(error => ({ verified: error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status) ? false : null }))
        .finally(() => window.clearTimeout(timeout));
      botAttempts.current.set(key, attempt);
    }
    void attempt.then(({verified, timezone}) => {
      if (cancelled || epoch !== sessionEpoch.current || activeOwner.current !== owner || `${owner}:${loadToken() ?? ''}` !== identity) return;
      if (verified && timezone && revision === userRevision.current) {
        setUser(current => current?.id === owner ? {...current, timezone} : current);
      }
      if (verified === null) {
        // A transport failure is not rejected identity. Preserve only an already
        // verified, account-scoped, unexpired pending intent; never dispatch it
        // until a fresh server check succeeds. One 8s attempt per document,
        // then an explicit reload retry (no background loop or wrong-home route).
        botIntents.pending(owner);
        setBotFailed(identity);
        failStartup();
        return;
      }
      if (verified) botIntents.accept(owner, initData);
      else botIntents.clear(); // Never trust SDK/URL identity after rejected verification.
      setBotChecked(identity);
    });
    return () => { cancelled = true; };
  }, [status, user?.id, botIdentity]);
  const botLaunchReady = !user || botChecked === botIdentity;

  const value = useMemo<SessionValue>(
    () => ({ user, status, botLaunchReady, loginWithTelegram, loginAsDev, logout, updateUser, refresh }),
    [user, status, botLaunchReady, loginWithTelegram, loginAsDev, logout, updateUser, refresh],
  );

  return <SessionContext.Provider value={value}>{children}
    {botIdentity && botFailed === botIdentity && !document.getElementById('startup-splash') ? <main role="alert">
      <p>Не удалось проверить переход из Telegram. Проверьте соединение и повторите.</p>
      <button type="button" className="b wide" onClick={() => window.location.reload()}>Повторить</button>
    </main> : null}
  </SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession вне SessionProvider');
  return value;
}

/** Внутри защищённых экранов пользователь гарантированно есть. */
export function useUser(): User {
  const { user } = useSession();
  if (!user) throw new Error('Нет пользователя в защищённом экране');
  return user;
}
